import qs from 'qs';

import type {
  CreateClaimCampaignBody,
  CreateClaimCampaignResponse,
  DeleteClaimCampaignResponse,
  DeleteClaimCodeResponse,
  GetClaimCampaignResponse,
  InternalSubmitClaimBody,
  InternalSubmitClaimResponse,
  ListClaimCampaignsParams,
  ListClaimCampaignsResponse,
  ListClaimCodesParams,
  ListClaimCodesResponse,
  ListClaimRecordsParams,
  ListClaimRecordsResponse,
  MatchCheckDefinition,
  TestMatchCheckResponse,
  UpdateClaimCampaignBody,
  UpdateClaimCampaignResponse,
  UpdateClaimCodeBody,
  UpdateClaimCodeResponse,
  UploadClaimCodesBody,
  UploadClaimCodesResponse,
} from '~/types/claimCampaignTypes';

import { request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

/**
 * Lists all claim campaigns with optional filters and pagination.
 */
export async function listClaimCampaigns(
  params: ListClaimCampaignsParams,
  options: RequestOptions = {},
): Promise<ListClaimCampaignsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/all?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Creates a new claim campaign.
 */
export async function createClaimCampaign(body: CreateClaimCampaignBody): Promise<CreateClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Fetches a single claim campaign by ID.
 */
export async function getClaimCampaign(campaignId: string): Promise<GetClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'GET',
  });
}

/**
 * Updates an existing claim campaign.
 */
export async function updateClaimCampaign(
  campaignId: string,
  body: UpdateClaimCampaignBody,
): Promise<UpdateClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Deletes a claim campaign and all related data.
 */
export async function deleteClaimCampaign(campaignId: string): Promise<DeleteClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'DELETE',
  });
}

/**
 * Uploads claim codes to a campaign.
 */
export async function uploadClaimCodes(
  campaignId: string,
  body: UploadClaimCodesBody,
): Promise<UploadClaimCodesResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Lists codes for a campaign with optional status filter and pagination.
 */
export async function listClaimCodes(
  campaignId: string,
  params: ListClaimCodesParams,
  options: RequestOptions = {},
): Promise<ListClaimCodesResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Lists claim records for a campaign with optional status filter and pagination.
 */
export async function listClaimRecords(
  campaignId: string,
  params: ListClaimRecordsParams,
  options: RequestOptions = {},
): Promise<ListClaimRecordsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/claims?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Submits a claim on behalf of a customer (admin internal claim).
 */
export async function submitInternalClaim(
  campaignId: string,
  body: InternalSubmitClaimBody,
): Promise<InternalSubmitClaimResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-claim`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Updates a claim code for a campaign (admin).
 */
export async function updateClaimCode(
  campaignId: string,
  codeId: string,
  body: UpdateClaimCodeBody,
): Promise<UpdateClaimCodeResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes/${codeId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Deletes a claim code from a campaign (admin).
 */
export async function deleteClaimCode(campaignId: string, codeId: string): Promise<DeleteClaimCodeResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes/${codeId}`, {
    method: 'DELETE',
  });
}

/**
 * Runs a dry-run match-check with the given BQ query and eligibility checks.
 */
export async function testMatchCheck(body: {
  bqQuery: string;
  checks: Array<MatchCheckDefinition>;
  customerId: string;
}): Promise<TestMatchCheckResponse> {
  return request(`${BASE_PATH}/claims/campaigns/test-match-check`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}
